<template>
    <el-dialog
        :visible.sync="dialogVisible"
        :title="fileName || 'PDF预览'"
        width="1080px"
        custom-class="pdf-preview-dialog"
        append-to-body
        :close-on-click-modal="false"
        :close-on-press-escape="true"
        @closed="resetPreview"
    >
        <div v-loading="loading" class="pdf-preview-content">
            <div v-if="errorMessage" class="pdf-preview-error">{{ errorMessage }}</div>
            <template v-else>
                <div ref="canvasContainer" class="pdf-preview-canvas-wrap">
                    <canvas v-for="page in totalPages" :key="page" ref="canvas" class="pdf-preview-canvas"></canvas>
                </div>
            </template>
        </div>
    </el-dialog>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist/webpack'
import { fetchAttachmentBlob } from '@/utils/attachment'

export default {
    name: 'PdfPreview',
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        file: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            loading: false,
            errorMessage: '',
            pdfDocument: null,
            totalPages: 0,
            renderTask: null,
        }
    },
    computed: {
        dialogVisible: {
            get() {
                return this.visible
            },
            set(value) {
                this.$emit('update:visible', value)
            },
        },
        fileName() {
            return (this.file && this.file.name) || 'PDF预览'
        },
    },
    watch: {
        visible(value) {
            if (value) {
                this.loadPdf()
            }
        },
    },
    mounted() {
        if (this.visible) this.loadPdf()
    },
    beforeDestroy() {
        this.destroyPdfDocument()
    },
    methods: {
        async loadPdf() {
            if (!this.file || (!this.file.url && !this.file.blob) || this.loading) return

            this.resetPreview()
            this.loading = true
            try {
                const blob = this.file.blob || await fetchAttachmentBlob(this.file.url)
                const data = await blob.arrayBuffer()
                const loadingTask = pdfjsLib.getDocument({ data })
                this.pdfDocument = await loadingTask.promise
                this.totalPages = this.pdfDocument.numPages
                await this.$nextTick()
                await this.renderAllPages()
            } catch (error) {
                this.errorMessage = 'PDF预览失败，请下载后查看'
            } finally {
                this.loading = false
            }
        },
        async renderAllPages() {
            if (!this.pdfDocument || !this.$refs.canvas || !this.$refs.canvasContainer) return

            const canvases = Array.isArray(this.$refs.canvas) ? this.$refs.canvas : [this.$refs.canvas]
            for (let pageNumber = 1; pageNumber <= this.totalPages; pageNumber += 1) {
                if (!this.pdfDocument) return
                await this.renderPage(pageNumber, canvases[pageNumber - 1])
            }
        },
        async renderPage(pageNumber, canvas) {
            if (!canvas || !this.$refs.canvasContainer) return

            const page = await this.pdfDocument.getPage(pageNumber)
            const originalViewport = page.getViewport({ scale: 1 })
            const containerWidth = this.$refs.canvasContainer.clientWidth - 40
            const scale = Math.min(1.5, Math.max(0.8, containerWidth / originalViewport.width))
            const viewport = page.getViewport({ scale })
            const outputScale = window.devicePixelRatio || 1
            const context = canvas.getContext('2d')

            canvas.width = Math.floor(viewport.width * outputScale)
            canvas.height = Math.floor(viewport.height * outputScale)
            canvas.style.width = `${Math.floor(viewport.width)}px`
            canvas.style.height = `${Math.floor(viewport.height)}px`
            context.setTransform(outputScale, 0, 0, outputScale, 0, 0)

            this.renderTask = page.render({
                canvasContext: context,
                viewport,
            })
            await this.renderTask.promise
            this.renderTask = null
        },
        resetPreview() {
            if (this.renderTask) {
                this.renderTask.cancel()
                this.renderTask = null
            }
            this.destroyPdfDocument()
            this.loading = false
            this.errorMessage = ''
            this.totalPages = 0
        },
        destroyPdfDocument() {
            if (!this.pdfDocument) return
            this.pdfDocument.destroy()
            this.pdfDocument = null
        },
    },
}
</script>

<style lang="less">
.pdf-preview-dialog {
    position: absolute;
    top: 50%;
    left: 50%;
    display: flex;
    flex-direction: column;
    max-width: calc(100vw - 48px);
    max-height: 90vh;
    margin: 0 !important;
    transform: translate(-50%, -50%);
}

.pdf-preview-dialog .el-dialog__header {
    position: relative;
    z-index: 2;
    flex: none;
    padding: 20px 24px;
    border-bottom: 1px solid #ebeef5;
    background: #fff;
}

.pdf-preview-dialog .el-dialog__title {
    display: block;
    padding-right: 32px;
    overflow: hidden;
    font-size: 16px;
    line-height: 24px;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.pdf-preview-dialog .el-dialog__headerbtn {
    top: 17px;
    right: 31px;
    z-index: 3;
    font-size: 27px;
}

.pdf-preview-dialog .el-dialog__headerbtn .el-dialog__close {
    color: #666;
}

.pdf-preview-dialog .el-dialog__headerbtn:hover .el-dialog__close {
    color: #2266FF;
}

.pdf-preview-dialog .el-dialog__body {
    flex: 1;
    min-height: 0;
    padding: 0 24px 24px;
    overflow: hidden;
}

.pdf-preview-content {
    height: calc(90vh - 105px);
    min-height: 360px;
}

.pdf-preview-canvas-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    height: 100%;
    padding: 20px;
    overflow: auto;
    background: #f5f7fa;
}

.pdf-preview-canvas {
    display: block;
    margin-bottom: 20px;
    background: #fff;
    box-shadow: 0 1px 6px rgba(0, 0, 0, .2);
}

.pdf-preview-error {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 520px;
    color: #f56c6c;
}
</style>
